import React from "react";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer
} from "recharts";
import { Box } from "@mui/material";

// Color palettes
const currencyColors = {
  USD: "#00D9FF",
  EUR: "#FF1CBE",
  GBP: "#FFD60A",
  SIKI: "#4CAF50",
  HBAR: "#9C27B0",
  Others: "#FF5722"
};

const tipLevelColors = [
  "#00D9FF",
  "#FF1CBE",
  "#FFD60A",
  "#4CAF50",
  "#FF5722",
  "#9C27B0",
  "#2196F3",
  "#00BCD4"
];

const RechartsDoughnutChart = ({ id, currency, tab, tip }) => {
  // Transform data based on type (currency or tip levels)
  const transformData = () => {
    if (currency) {
      // Currency distribution data
      return Object.entries(currency).map(([key, value]) => ({
        name: key,
        value: value || 0
      }));
    } else if (tip) {
      // Tip levels data
      return Object.entries(tip).map(([key, value]) => ({
        name: `Level ${key}`,
        value: value || 0
      }));
    }
    return [];
  };

  const data = transformData();
  const colors = currency ? Object.values(currencyColors) : tipLevelColors;

  // Custom tooltip
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const total = data.reduce((sum, entry) => sum + entry.value, 0);
      const percentage = ((payload[0].value / total) * 100).toFixed(1);
      
      return (
        <Box
          sx={{
            background: "#1a1a1a",
            border: "1px solid #333",
            borderRadius: "8px",
            padding: "10px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.5)"
          }}
        >
          <p style={{ color: "#fff", margin: 0 }}>{payload[0].name}</p>
          <p style={{ color: payload[0].fill, margin: "5px 0 0 0" }}>
            {currency ? 'Amount: $' : 'Count: '}{payload[0].value.toLocaleString()}
          </p>
          <p style={{ color: "#888", margin: "5px 0 0 0" }}>
            {percentage}%
          </p>
        </Box>
      );
    }
    return null;
  };

  // Custom label
  const renderCustomLabel = (entry) => {
    const total = data.reduce((sum, item) => sum + item.value, 0);
    const percent = ((entry.value / total) * 100).toFixed(0);
    return percent > 5 ? `${percent}%` : '';
  };

  // Custom legend
  const renderLegend = (props) => {
    const { payload } = props;
    const total = data.reduce((sum, entry) => sum + entry.value, 0);
    
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: 1,
          fontSize: '12px'
        }}>
          {payload.map((entry, index) => {
            const value = data.find(d => d.name === entry.value)?.value || 0;
            const percentage = ((value / total) * 100).toFixed(1);
            
            return (
              <Box key={`item-${index}`} sx={{ 
                display: 'flex', 
                alignItems: 'center',
                color: '#888',
                gap: 1
              }}>
                <span style={{
                  display: 'inline-block',
                  width: 12,
                  height: 12,
                  backgroundColor: entry.color,
                  borderRadius: '50%',
                  flexShrink: 0
                }} />
                <span style={{ flex: 1 }}>{entry.value}</span>
                <span style={{ color: '#666' }}>{percentage}%</span>
              </Box>
            );
          })}
        </Box>
        <Box sx={{ 
          textAlign: 'center', 
          mt: 2, 
          pt: 2, 
          borderTop: '1px solid #333',
          color: '#888'
        }}>
          <strong>Total: {currency ? '$' : ''}{total.toLocaleString()}</strong>
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{ width: "100%", height: "500px", backgroundColor: "#090909", p: 2 }}>
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={data}
            cx="50%"
            cy="45%"
            labelLine={false}
            label={renderCustomLabel}
            outerRadius={100}
            innerRadius={60}
            fill="#8884d8"
            dataKey="value"
            paddingAngle={2}
          >
            {data.map((entry, index) => (
              <Cell 
                key={`cell-${index}`} 
                fill={currency ? (currencyColors[entry.name] || colors[index % colors.length]) : colors[index % colors.length]} 
              />
            ))}
          </Pie>
          <Tooltip content={<CustomTooltip />} />
          <Legend 
            content={renderLegend}
            wrapperStyle={{ paddingTop: "20px" }}
          />
        </PieChart>
      </ResponsiveContainer>
    </Box>
  );
};

export default RechartsDoughnutChart;